import type * as React from 'react';
import type { UserLetter } from '../../../MedicareProAPI/Entities/User/UserLetter';
import { colorLegend } from '../../Constants/AgentReportingTemplates';

interface TemplateBrowserProps {
  letters: any;
  selectedTemplate?: UserLetter;
  onNewTemplate: () => void;
  onSelect: (template: UserLetter) => void;
}

/**
 * Component that lists saved templates for editing
 *
 * @component
 * @author Andrew Brown <abrown@medicareprocrm.com>
 * @date Created: 2025-05-20
 * @lastUpdated
 * @version 1.1.0
 */

export const TemplateBrowser = ({
  letters,
  selectedTemplate,
  onNewTemplate,
  onSelect,
}: TemplateBrowserProps): React.JSX.Element => {
  const folderNames = Object.keys(letters);

  return (
    <>
      <h6 className="font-weight-bold mb-3">Templates</h6>

      <div
        onClick={onNewTemplate}
        className="btn btn-sm border border-primary text-primary mb-4 cp"
      >
        <i className="fas fa-plus-circle mr-2"></i>New template
      </div>

      <div id="templateAccordion">
        {Array.isArray(folderNames) &&
          folderNames.length > 0 &&
          folderNames.map((folder, folderIndex) => {
            const folderId = `folder-${folderIndex}`;
            const collapseId = `collapse-${folderIndex}`;
            const templates = letters[folder]?.getItems?.() || [];

            const isActiveFolder =
              selectedTemplate &&
              templates.some((t) => t.getLetterId() === selectedTemplate.getLetterId());

            return (
              <div key={folderId} className="mb-2 border-b">
                {/* Folder Header */}
                <div
                  id={folderId}
                  className="px-3 py-2 font-weight-bold bg-contrast rounded d-flex align-items-center cp calMess"
                  data-toggle="collapse"
                  data-target={`#${collapseId}`}
                  aria-expanded={isActiveFolder ? 'true' : 'false'}
                  aria-controls={collapseId}
                >
                  <i className="far fa-folder text-success mr-2"></i>
                  {folder}
                </div>

                {/* Template List */}
                <div
                  id={collapseId}
                  className={`collapse ${isActiveFolder ? 'show' : ''}`}
                  aria-labelledby={folderId}
                  data-parent="#templateAccordion"
                >
                  <div className="px-3 py-2">
                    {templates.length > 0 ? (
                      <ul className="list-group list-group-flush">
                        {templates.map((template: UserLetter, templateIndex: number) => {
                          const type = template.getLetterType().toLowerCase();
                          const badgeClass = colorLegend[type] || 'secondary';

                          return (
                            <li
                              key={template.getLetterId() || templateIndex}
                              className="list-group-item px-3 py-2 d-flex justify-between align-items-center cp savedTemplate"
                              onClick={() => onSelect(template)}
                              style={{
                                borderLeft: `4px solid var(--bs-${badgeClass})`,
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <i className="far fa-file-alt text-primary mr-2"></i>
                                <span>{template.getLetterTitle()}</span>
                              </div>
                              <div>
                                <span
                                  className={`ml-2 badge badge-${badgeClass} badge-pill liBadge`}
                                  style={{ fontSize: '8pt' }}
                                >
                                  {template.getLetterType()}
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div className="text-muted small">No templates in this folder.</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};
